// Histogram binning per the client spec (21DeviceAppsFinalBuildsTODOS/Sheet1.html rows 16-78).
//
//   Step A  min = lowest value, max = highest value
//   Step B  binCount = round(dataPoints / 2)
//   Step C  binWidth = (max - min) / binCount
//           bin k: min + k*w <= x < min + (k+1)*w, last bin inclusive of max
//
// The spec's prose example ("5-8, 9-12, 13-16, 17-20" at "bin width ~= 4") contradicts its own Step C,
// which gives (20-5)/4 = 3.75 -> 5-8.75, 8.75-12.5, ... The prose is a hand-rounding for integer data and
// would leave gaps between bins that silently drop values. The reference screenshots use continuous
// boundaries (upper of bin k == lower of bin k+1) and are what we build to. See docs/analysis/.

// The spec's "bins = data points / 2" is written for the 8-13 point examples in the doc and does not
// scale: 40 points would give 20 bins and 118 would give 59, producing a chart wider than the page.
// Past this ceiling the bin count holds and the bin width grows instead, which is the same trade the
// rule makes anyway — fewer, wider bins. Only binds above 20 data points, so every worked example in
// the spec (7 -> 4, 10 -> 5, 11 -> 6) is unaffected.
// Tuned against BarChart's ~95px-per-bar sizing: 10 bars ~= 950px, about the widest the PDF fits.
export const MAX_BINS = 10;

// Bin labels are whole numbers, rounded to the nearest integer (0.22 -> 0, 0.5 -> 1). Adjacent bins
// share a boundary (bin k's upper is bin k+1's lower, the same underlying number), so rounding both
// the same way keeps the axis continuous — no gaps or overlaps in the labels. Narrow-range data (e.g.
// parts measured 49.91-50.13) collapses to repeated labels like "50-50"; that is accepted, and the
// chart caption states the rounding. Binning uses the raw boundaries below, not these labels, so
// counts are unaffected — only the displayed range rounds.
const fmt = (x) => String(Math.round(x));

/**
 * @param {Array<number|string>} rawValues one entry per observation
 * @returns {{n, min, max, binCount, binWidth, degenerate, bins: Array<{lo, hi, label, count}>}|null}
 *          null when there is nothing to plot. Bins are returned in ascending order and include
 *          empty ones (count 0) so the x-axis stays numerically continuous.
 */
export function computeHistogram(rawValues) {
  // Number('') and Number(null) are both 0, which would drag min to 0 and corrupt every bin
  // boundary. Reject blanks before coercing.
  const values = (rawValues || [])
    .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
    .map(Number)
    .filter((v) => Number.isFinite(v))
    .sort((a, b) => a - b);

  const n = values.length;
  if (n === 0) return null;

  const min = values[0];
  const max = values[n - 1];

  // Spec: "Bins = Data points / 2 (rounded)". 11/2=5.5 -> 6, 9/2=4.5 -> 5, 13/2=6.5 -> 7.
  const binCount = Math.min(MAX_BINS, Math.max(1, Math.round(n / 2)));

  // Every value identical -> range 0 -> binWidth 0 -> division by zero downstream.
  if (max === min) {
    return {
      degenerate: true,
      n, min, max, binCount: 1, binWidth: 0,
      bins: [{ lo: min, hi: max, label: fmt(min), count: n }],
    };
  }

  const binWidth = (max - min) / binCount;

  const bins = Array.from({ length: binCount }, (_, i) => {
    const lo = min + i * binWidth;
    const hi = i === binCount - 1 ? max : min + (i + 1) * binWidth;
    return { lo, hi, label: `${fmt(lo)}-${fmt(hi)}`, count: 0 };
  });

  // Index arithmetically rather than walking boundaries, so the "last bin inclusive of max" rule
  // survives floating-point drift.
  //
  // The epsilon is load-bearing, not defensive padding. A value sitting exactly on a boundary belongs
  // to the bin above it (spec: min + k*w <= x < min + (k+1)*w), but binary floating point puts it just
  // below: for min=49.95, w=0.03, the value 49.98 gives (49.98-49.95)/0.03 = 0.9999999999999, which
  // floors to 0 — one bin too low. Nudging the quotient recovers the intended boundary. It is applied
  // to the quotient (bounded by binCount <= 10), so it cannot misplace a value that is genuinely
  // inside a bin — only one already within float noise of the edge.
  const EPSILON = 1e-9;
  for (const v of values) {
    bins[Math.min(binCount - 1, Math.floor((v - min) / binWidth + EPSILON))].count++;
  }

  return { degenerate: false, n, min, max, binCount, binWidth, bins };
}
